/**
 * The measuring tool (10-web-admin § 6.2 step 2).
 *
 * Two clicks and a real-world length give `px_per_cm`. Pure, because a wrong
 * scale silently makes every desk the wrong size on a plan that still looks
 * plausible — the kind of error nobody notices until a sticker is printed.
 */

export interface Point {
  x: number;
  y: number;
}

export function pixelDistance(a: Point, b: Point): number {
  return Math.hypot(b.x - a.x, b.y - a.y);
}

/** German decimals use a comma; a plan measured as "12,40" must not parse to 12. */
export function parseMetres(input: string): number | null {
  const normalised = input.trim().replace(',', '.');
  if (!/^\d+(\.\d+)?$/.test(normalised)) return null;
  const value = Number(normalised);
  return value > 0 ? value : null;
}

export interface ScaleResult {
  pxPerCm: number;
  /** What one FlexDesk's 120 cm becomes, for the panel's sanity line. */
  deskWidthPx: number;
}

/**
 * `null` when the measurement cannot produce a scale — two identical points,
 * or a length of zero. Refusing is better than a division that yields Infinity
 * and a plan whose desks are each a single pixel wide.
 */
export function scaleFrom(a: Point, b: Point, realMetres: number, deskWidthCm = 120): ScaleResult | null {
  const px = pixelDistance(a, b);
  const cm = realMetres * 100;
  if (px <= 0 || cm <= 0) return null;

  const pxPerCm = px / cm;
  return { pxPerCm, deskWidthPx: Math.round(deskWidthCm * pxPerCm) };
}

/** A non-breaking space before the unit, as everywhere else (03 § 2). */
const NBSP = '\u00a0';

/** The label on the reference line: "12,40 m = 1.244 px". */
export function formatMeasurement(metres: number, px: number): string {
  const length = new Intl.NumberFormat('de-DE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(metres);
  const pixels = new Intl.NumberFormat('de-DE').format(Math.round(px));
  return `${length}${NBSP}m = ${pixels}${NBSP}px`;
}
